import {
  Alert,
  Center,
  Loader,
  NavLink,
  ScrollArea,
  ThemeIcon,
} from '@mantine/core';
import { useScrollAreaMask } from '@/shared/lib/hooks/use-scroll-area-mask';
import {
  memo,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import IconFile from '@tabler/icons-react/dist/esm/icons/IconFile.mjs';
import IconFolder from '@tabler/icons-react/dist/esm/icons/IconFolder.mjs';
import { SETTINGS } from '@/shared/config/routes';
import { Tree } from '@/shared/lib/utils/tree/types';
import { treeMap } from '@/shared/lib/utils/tree/tree-map';
import type { NavigationNode } from '../../model/navigation-node';
import classes from './menu.module.css';

type MenuNode = {
  icon?: ReactNode;
  value?: string;
  label: string;
  key: string;
  children?: MenuNode[];
};

const renderMenuIcon = (
  key: string,
  icon: ReactNode | undefined,
  hasChildren: boolean,
) => (
  <ThemeIcon variant='light' size={30} data-testid={`navigation-icon-${key}`}>
    {icon ?? (hasChildren ? <IconFolder size={18} /> : <IconFile size={18} />)}
  </ThemeIcon>
);

// Returns the list of ancestor keys (branch nodes only) leading to the
// node whose `value` matches currentRoute, or null if not found.
function findActivePath(
  tree: Tree<any>,
  currentRoute: string,
  path: string[] = [],
): string[] | null {
  for (const node of tree as MenuNode[]) {
    if (node.children) {
      const nextPath = [...path, node.key];
      const found = findActivePath(node.children, currentRoute, nextPath);
      if (found) return found;
    } else if (node.value === currentRoute) {
      return path;
    }
  }
  return null;
}

function generateMenu(
  tree: Tree<any>,
  currentRoute: string,
  openedKeys: Set<string>,
  onToggle: (key: string, opened: boolean) => void,
) {
  return treeMap(tree, 'children', (x) => {
    if (x.children) {
      return (
        <NavLink
          key={x.key}
          data-testid={`navigation-item-${x.key}`}
          label={x.label}
          variant={'light'}
          childrenOffset={30}
          classNames={{
            children: classes.subtree,
          }}
          opened={openedKeys.has(x.key)}
          onChange={(opened) => onToggle(x.key, opened)}
          leftSection={renderMenuIcon(x.key, x.icon, true)}
        >
          {generateMenu(x.children, currentRoute, openedKeys, onToggle)}
        </NavLink>
      );
    } else {
      return (
        <NavLink
          data-testid={`navigation-item-${x.key}`}
          label={x.label}
          key={x.key}
          variant={'light'}
          component={Link}
          to={x.value}
          active={x.value === currentRoute}
          leftSection={renderMenuIcon(x.key, x.icon, false)}
        />
      );
    }
  });
}

type NavMenuProps = {
  currentRoute?: string;
  error: unknown;
  isFetching: boolean;
  navigation: NavigationNode[];
};

export const NavMenu = memo(
  ({ currentRoute, error, isFetching, navigation }: NavMenuProps) => {
    const { t, i18n } = useTranslation();

    const tree = useMemo(() => {
      return treeMap(navigation, 'children', (x) => ({
        icon: x.icon,
        value: x.to,
        label: t(x.label ?? '', { ns: 'navigation' }),
        key: x.label ?? x.to ?? '',
      }));
    }, [navigation, i18n.language]);

    // Persisted set of manually/auto opened branch keys. Survives re-renders
    // and route changes since it lives in component state, not derived fresh
    // from props each render.
    const [openedKeys, setOpenedKeys] = useState<Set<string>>(new Set());
    const defaultOpenApplied = useRef(false);

    useEffect(() => {
      if (defaultOpenApplied.current) return;

      const settingsNode = tree.find(
        (node) => node.value === SETTINGS && node.children,
      );
      if (!settingsNode) return;

      defaultOpenApplied.current = true;
      setOpenedKeys((prev) => new Set(prev).add(settingsNode.key));
    }, [tree]);

    // Whenever the active route changes, make sure the branch leading to it
    // is expanded (without collapsing anything the user already opened).
    useEffect(() => {
      if (!currentRoute || !tree) return;
      const activePath = findActivePath(tree, currentRoute);
      if (!activePath || activePath.length === 0) return;

      setOpenedKeys((prev) => {
        const next = new Set(prev);
        let changed = false;
        for (const key of activePath) {
          if (!next.has(key)) {
            next.add(key);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, [tree, currentRoute]);

    const handleToggle = (key: string, opened: boolean) => {
      setOpenedKeys((prev) => {
        const next = new Set(prev);
        if (opened) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    };

    const items = useMemo(() => {
      if (!tree) return null;
      return generateMenu(tree, currentRoute, openedKeys, handleToggle);
    }, [tree, currentRoute, openedKeys]);

    const scrollConfig = useScrollAreaMask();
    const config = {
      ...scrollConfig,
      classNames: { ...scrollConfig.classNames, root: classes.links },
    };

    if (isFetching) {
      return (
        <Center data-testid='navigation-menu-loader' p='md'>
          <Loader />
        </Center>
      );
    }

    if (error) {
      return (
        <Center data-testid='navigation-menu-error' p='md'>
          <Alert
            color='red'
            variant='light'
            title={t('menuError.title', { ns: 'navigation' })}
            role='alert'
          >
            {t('menuError.description', { ns: 'navigation' })}
          </Alert>
        </Center>
      );
    }

    return <ScrollArea {...config}>{items}</ScrollArea>;
  },
);